import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons/faExternalLinkAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Select from "antd/es/select";
import Row from "antd/lib/grid/row";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByImport } from "@mapmycustomers/shared/types/entity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { Button, Modal } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { selectLayoutId } from "@app/scene/settings/component/Imports/store/actions";
import { getEntityType, getLayoutId } from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import getLayoutModelByEntityType from "@app/util/layout/impl";

import styles from "./LayoutSelectorModal.module.scss";

interface Props {
  entityType: EntityTypesSupportedByImport;
  layoutId?: FormLayout["id"];
  onHide: (layoutSelected?: boolean) => void;
  selectLayoutId: typeof selectLayoutId;
}

const LayoutSelectorModal: React.FC<Props> = ({ entityType, layoutId, onHide, selectLayoutId }) => {
  const intl = useIntl();
  const layouts = getLayoutModelByEntityType(entityType).layouts;

  const [selectedLayoutId, setSelectedLayoutId] = useState<FormLayout["id"] | undefined>(layoutId);

  const handleConfirm = useCallback(() => {
    if (selectedLayoutId) {
      selectLayoutId(selectedLayoutId);
      onHide(true);
    }
  }, [onHide, selectLayoutId, selectedLayoutId]);

  if (!selectedLayoutId) {
    return null;
  }

  return (
    <Modal
      footer={null}
      onCancel={() => onHide()}
      open
      title={intl.formatMessage({
        id: "settings.newImports.uploadStem.layoutSelectorModal.title",
        defaultMessage: "Confirm Layout",
        description: "Title of layout selector modal on import first step",
      })}
    >
      <div className={styles.container}>
        <div>
          {intl.formatMessage({
            id: "settings.newImports.uploadStem.layoutSelectorModal.text",
            defaultMessage:
              "Any fields marked as required by the selected layout will be required for import.",
            description: "text of layout selector modal on import first step",
          })}
        </div>
        <div>
          <ButtonLink
            className={styles.link}
            href={`${Path.SETTINGS}/${SettingPath.FORMS_CREATE}/${entityType}`}
            target="_blank"
          >
            {intl.formatMessage({
              id: "settings.newImports.uploadStem.layoutSelectorModal.link",
              defaultMessage: "Go to Layouts",
              description: "Link text of layout selector modal on import first step",
            })}
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </ButtonLink>
        </div>
        <Select<FormLayout["id"]>
          className={styles.select}
          onChange={setSelectedLayoutId}
          placeholder={intl.formatMessage({
            id: "settings.newImports.uploadStem.layoutSelectorModal.placeholder",
            defaultMessage: "Select layout",
            description: "Placeholder of layout selector modal on import first step",
          })}
          value={selectedLayoutId}
        >
          {layouts.map((layout) => (
            <Select.Option key={layout.id} value={layout.id}>
              {layout.name}
            </Select.Option>
          ))}
        </Select>
        <Row justify="end">
          <Col>
            <Button onClick={handleConfirm} type="primary">
              {intl.formatMessage({
                id: "settings.newImports.uploadStem.layoutSelectorModal.confirm",
                defaultMessage: "Continue with Import",
                description:
                  "Continue with Import button text of layout selector modal on import first step",
              })}
            </Button>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  layoutId: getLayoutId(state),
});

const mapDispatchToProps = {
  selectLayoutId,
};

export default connect(mapStateToProps, mapDispatchToProps)(LayoutSelectorModal);
